import { useEffect, useRef, useState } from 'react';

import { useQueryClient } from 'react-query';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { DocumentFiltersPopup } from './doc-filters';
import { DocSortPopup } from './doc-sort';
import { Permission } from '../../../../../../../../../../backend/src/auth/permissions/permission';
import { DeleteDocumentsDialog } from '../../../../docs/components/delete-documents-dialog';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useDeleteMatterDocs } from '@/api/mutations/useDeleteMatterDocs';
import { useStoreSubsetDocIds } from '@/api/mutations/useStoreSubsetDocIds';
import { useGetDeletionStatus } from '@/api/queries/useGetDeletionStatus';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { QueryEntity } from '@/api/types';
import { Issue } from '@/api/types/backend-dto-types';
import { PermissionGate } from '@/hooks/useHasPermission';
import { CreateRelativityListDialog } from '@/new/components/dialogs/relativity/create-relativity-list-dialog';
import { DocChronologyDropdown } from '@/new/components/doc-chronology-dropdown';
import { FilterPills } from '@/new/components/filter-pills';
import { FilterPopoverContent } from '@/new/components/filter-popover-content';
import { Icon } from '@/new/components/icon';
import { NaturalLanguageSearchBox } from '@/new/components/natural-language-search-bar';
import { OverflowMenu } from '@/new/components/overflow-menu';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { Pagination } from '@/new/components/pagination';
import { Button } from '@/new/components/shadcn/button';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import { DropdownMenuItem } from '@/new/components/shadcn/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/new/components/shadcn/popover';
import { Switch } from '@/new/components/shadcn/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { SubsetDropdown } from '@/new/components/subset-dropdown';
import { cn } from '@/new/lib/utils';
import { SelectFromListDialog } from '@/new/pages/matters/:matterId/subsets/new/components/select-from-list-dialog';
import { useMatterProcessingStore } from '@/new/stores/matter-processing-store';
import { DocFilters } from '@/types';
import { FilterPill } from '@/utils/buildFilterPills';

const selectFromListSource = 'docs_page';

// folderId is the folder structure itself — being inside a folder is the only filter compatible
// with the folder view.
const FOLDER_COMPATIBLE_FILTER_KEYS = new Set<keyof DocFilters>(['folderId']);

// we have to do this because folder id gets treated like a filter, so if we don't check everything
// but that, it will go back to flat when you click on a folder
function hasFlatForcingFilters(filters?: DocFilters): boolean {
  if (!filters) return false;

  return (Object.entries(filters) as [keyof DocFilters, DocFilters[keyof DocFilters]][]).some(([key, value]) => {
    if (FOLDER_COMPATIBLE_FILTER_KEYS.has(key)) return false;
    if (value == null) return false;
    return Array.isArray(value) ? value.length > 0 : true;
  });
}

interface DocumentEditorToolbarProps {
  matterId: string;
  subsetId: string;
  isAllDocuments?: boolean;
  matterIssues?: Issue[];
  isMatterDraft?: boolean;
  isSampleMatter?: boolean;
  onOpenDownload: () => void;
  globalFilter: string;
  setGlobalFilter: (value: string) => void;
  onSearchCall: (value: string, useNaturalLanguage: boolean) => void;
  clearSearch: () => void;
  processingNL?: boolean;
  currentPage: number;
  totalPages?: number;
  hasMore?: boolean;
  prevPage: () => void;
  nextPage: () => void;
  goToPage: (page: number) => void;
  pageSize: number;
  onPageSizeChange: (pageSize: number) => void;
  hasActiveFilters: boolean;
  totalCount?: number;
  totalUnfilteredCount?: number;
  filterPills: FilterPill[];
  onClearAllFilters: () => void;
  filters: DocFilters;
  setFilters: (filters: DocFilters) => void;
  allDocumentTypes: string[];
  docIssues: string[];
  sortBy: string;
  setSortBy: (value: string) => void;
  sortOrder: 'ASC' | 'DESC';
  setSortOrder: (value: 'ASC' | 'DESC') => void;
  clearSort: () => void;
  // Document selection
  selectedDocIds: Set<string>;
  allDocsOnPageChecked: boolean;
  toggleAllDocsHandler: () => Promise<void>;
  deselectAllDocsFromDb: () => void;
  deselectDocsFromDb: (docIds: string[]) => void;
  onAddSelectedDocIds: (docIds: string[]) => Promise<void>;
  isAllDocIdsLoading: boolean;
  isLoadingDocs: boolean;
  isFlattened: boolean;
  setIsFlattened: (value: boolean) => void;
}

export function DocTableHeader({
  matterId,
  subsetId,
  isAllDocuments,
  matterIssues,
  isMatterDraft,
  isSampleMatter,
  onOpenDownload,
  globalFilter,
  setGlobalFilter,
  onSearchCall,
  clearSearch,
  currentPage,
  totalPages,
  hasMore,
  prevPage,
  nextPage,
  goToPage,
  pageSize,
  onPageSizeChange,
  hasActiveFilters,
  processingNL,
  totalCount,
  totalUnfilteredCount,
  filterPills,
  onClearAllFilters,
  filters,
  setFilters,
  allDocumentTypes,
  docIssues,
  sortBy,
  setSortBy,
  sortOrder,
  setSortOrder,
  clearSort,
  // Document selection
  selectedDocIds,
  allDocsOnPageChecked,
  toggleAllDocsHandler,
  deselectAllDocsFromDb,
  deselectDocsFromDb,
  onAddSelectedDocIds,
  isAllDocIdsLoading,
  isLoadingDocs,
  isFlattened,
  setIsFlattened,
}: DocumentEditorToolbarProps) {
  const forcedFlatByFilters = hasFlatForcingFilters(filters);

  const { getProcessingState } = useMatterProcessingStore();
  const processingState = getProcessingState(matterId);
  const isProcessing = processingState?.hasProcessingRuns ?? false;

  const navigate = useNavigate();
  const storeDocIds = useStoreSubsetDocIds();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectFromListOpen, setSelectFromListOpen] = useState(false);
  const [createRelativityListOpen, setCreateRelativityListOpen] = useState(false);

  const { data: matter } = useGetMatterV2(matterId);
  const relativityWorkspaceId = matter?.relativityWorkspaceId ?? null;
  const isRelativityMatter = relativityWorkspaceId !== null;

  const { mutate: deleteDocs, isLoading: isDeleting } = useDeleteMatterDocs(matterId, (docIds) => {
    setDeleteDialogOpen(false);
    deselectDocsFromDb(docIds);
  });

  // Polls only while a deletion is in flight; drives the inline progress indicator below.
  const { data: deletionStatus } = useGetDeletionStatus(matterId);
  const inProgressDeletion = deletionStatus?.inProgress ?? null;
  const hasPostDeletionRun = deletionStatus?.hasPostDeletionRun ?? false;
  const hasDeletionInFlight = inProgressDeletion !== null || hasPostDeletionRun;

  // Best-effort cache invalidation when the deletion settles while the user is on this page.
  const queryClient = useQueryClient();
  const lastSeenInProgressId = useRef<string | null>(null);
  useEffect(() => {
    if (inProgressDeletion !== null) {
      lastSeenInProgressId.current = inProgressDeletion.id;
      return;
    }
    if (lastSeenInProgressId.current === null) return;
    lastSeenInProgressId.current = null;

    [
      QueryEntity.InconsistenciesByIds,
      QueryEntity.MatterOverview,
      QueryEntity.SubsetOverview,
      QueryEntity.ChronologyFacts,
      QueryEntity.Doc,
      QueryEntity.DocIds,
      QueryEntity.DocTotals,
      QueryEntity.Fact,
      QueryEntity.InconsistencyReport,
      QueryEntity.Messages,
      QueryEntity.Summary,
      QueryEntity.Threads,
      QueryEntity.KimThreads,
      QueryEntity.MatterDocsWithCases,
      QueryEntity.DeletedFiles,
    ].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));
  }, [inProgressDeletion, queryClient]);

  return (
    <PageContainer className="border-b pb-3">
      <PageHeader>
        <PageTitle text="Documents" showSubsetChip />

        <div className="flex flex-col gap-2.5">
          <div className="flex justify-end gap-2">
            {inProgressDeletion !== null && (
              <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-800">
                <Icon className="lucide--loader animate-spin text-amber-600 ease-in-out" />
                <span>
                  Deleting {inProgressDeletion.completedDocs ?? 0} / {inProgressDeletion.totalDocs ?? '?'} documents
                </span>
              </div>
            )}
            {isAllDocuments !== undefined &&
              (isAllDocuments ? (
                !isMatterDraft && (
                  <PermissionGate permission={Permission.DocumentUpload} uiMode="disabled">
                    {isSampleMatter ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span>
                            <Button variant="primary" disabled>
                              <Icon className="lucide--upload" />
                              Upload Documents
                            </Button>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>You can't upload documents to a sample Matter.</TooltipContent>
                      </Tooltip>
                    ) : matterIssues ? (
                      <Button asChild variant="primary">
                        <Link to={`/matters/${matterId}/docs/upload`}>
                          <Icon className="lucide--upload" />
                          Upload Documents
                        </Link>
                      </Button>
                    ) : (
                      <div className="flex items-center gap-2 px-2 text-xs text-gray-600">
                        <Icon className="lucide--info" />
                        <p>
                          <Link className="text-blue-500" to={`/matters/${matterId}/settings/issues`}>
                            Add Matter issues
                          </Link>
                          <span> to upload more documents</span>
                        </p>
                      </div>
                    )}
                  </PermissionGate>
                )
              ) : (
                <Button asChild variant="primary">
                  <Link
                    to={`/matters/${matterId}/subsets/${subsetId}/edit`}
                    onClick={() => {
                      trackCustomEvent(TrackingEventsV2.DocumentClickAdd);
                    }}
                  >
                    <Icon className="lucide--diff" />
                    Edit Documents in Subset
                  </Link>
                </Button>
              ))}

            <SubsetDropdown
              matterId={matterId}
              subsetId={subsetId}
              disabled={selectedDocIds.size === 0}
              triggerText="Subset"
              isLoading={storeDocIds.isLoading}
              onCreateNew={async () => {
                trackCustomEvent(TrackingEventsV2.SubsetOpenCreateNew, { matterId });
                try {
                  const result = await storeDocIds.mutateAsync({
                    matterId,
                    docIds: Array.from(selectedDocIds),
                  });
                  navigate(`/matters/${matterId}/subsets/new?factsQueryKey=${result.queryKey}`);
                } catch {
                  toast.error('Failed to prepare documents for subset creation');
                }
              }}
              getDocIds={async () => Array.from(selectedDocIds)}
            />

            <DocChronologyDropdown
              matterId={matterId}
              subsetId={subsetId}
              selectedDocIds={selectedDocIds}
              triggerText="Document Chronology"
              createDescription="Create a new document chronology from the selected documents"
              addDescription="Select an existing document chronology to add documents to"
            />

            <PermissionGate permission={Permission.DocumentDownload} uiMode="disabled">
              <Button disabled={!getProcessingState(matterId)?.hasCompletedRuns} onClick={onOpenDownload}>
                <Icon className="lucide--download" />
                <span className="font-medium">Download</span>
              </Button>
            </PermissionGate>

            <OverflowMenu ariaLabel="More document actions">
              <DropdownMenuItem
                onClick={() => {
                  setSelectFromListOpen(true);
                  trackCustomEvent(TrackingEventsV2.DocumentSelectFromListClicked, {
                    source: selectFromListSource,
                  });
                }}
              >
                <Icon className="lucide--list" />
                Select Documents From List
              </DropdownMenuItem>
              {isRelativityMatter && (
                <DropdownMenuItem
                  disabled={selectedDocIds.size === 0}
                  onClick={() => setCreateRelativityListOpen(true)}
                >
                  <Icon className="lucide--list-plus" />
                  Create list in Relativity{selectedDocIds.size > 0 ? ` (${selectedDocIds.size})` : ''}
                </DropdownMenuItem>
              )}
              {isAllDocuments && (
                <PermissionGate
                  permission={Permission.DocumentDelete}
                  disabledMessage="You do not have permission to delete documents"
                  uiMode="disabled"
                >
                  {hasDeletionInFlight ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div>
                          <DropdownMenuItem disabled onClick={(e) => e.preventDefault()}>
                            <Icon className="lucide--trash-2" />
                            Delete {selectedDocIds.size > 0 ? `${selectedDocIds.size} ` : ''}Document
                            {selectedDocIds.size === 1 ? '' : 's'}
                          </DropdownMenuItem>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="left" className="max-w-56" sideOffset={16}>
                        <p className="text-center">
                          {inProgressDeletion !== null
                            ? 'A document deletion is already in progress for this matter'
                            : 'Finishing up the previous deletion'}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <DropdownMenuItem
                      disabled={selectedDocIds.size === 0 || isProcessing}
                      onClick={() => setDeleteDialogOpen(true)}
                    >
                      <Icon className="lucide--trash-2" />
                      Delete {selectedDocIds.size > 0 ? `${selectedDocIds.size} ` : ''}Document
                      {selectedDocIds.size === 1 ? '' : 's'}
                    </DropdownMenuItem>
                  )}
                </PermissionGate>
              )}
            </OverflowMenu>
          </div>
        </div>
      </PageHeader>

      <div className="flex gap-2">
        <NaturalLanguageSearchBox
          value={globalFilter}
          onChange={setGlobalFilter}
          onSearchCall={onSearchCall}
          clearSearch={clearSearch}
          processingNL={processingNL}
          entityType="documents"
        />

        <Popover>
          <PopoverTrigger asChild>
            <Button>
              <Icon className="lucide--sort-asc" />
              <span>Sort</span>
            </Button>
          </PopoverTrigger>

          <PopoverContent side="left" className="mt-12 min-w-md" sideOffset={8}>
            <div className="-mx-4 -mt-3 rounded-t-md border-b bg-gray-50 p-4 font-medium text-gray-700">
              Document Sorting
            </div>

            <div className="mt-4 flex flex-col gap-4">
              <DocSortPopup
                activeSortBy={sortBy}
                activeSortOrder={sortOrder}
                setSortBy={setSortBy}
                setSortOrder={setSortOrder}
                hasActiveSearch={!!globalFilter && globalFilter.trim() !== ''}
              />
            </div>

            <div className="mt-4 flex w-full justify-end">
              <Button onClick={clearSort}>
                <span>Clear Sorting</span>
              </Button>
            </div>
          </PopoverContent>
        </Popover>

        <Popover>
          <PopoverTrigger asChild>
            <Button>
              <Icon className="lucide--funnel-plus" />
              <span>Add Filters</span>
            </Button>
          </PopoverTrigger>
          <FilterPopoverContent clearAllFilters={onClearAllFilters}>
            <DocumentFiltersPopup
              matterId={matterId}
              activeFilters={filters}
              setFilters={setFilters}
              allDocumentTypes={allDocumentTypes}
              docIssues={docIssues}
            />
          </FilterPopoverContent>
        </Popover>
      </div>

      <div>
        <FilterPills pills={filterPills} onClearAll={onClearAllFilters} />
      </div>

      <div className="flex items-center justify-between pt-4 pb-0.5">
        <div className="flex items-center gap-3 pl-1 text-xs text-gray-500">
          <Checkbox
            checked={allDocsOnPageChecked}
            onCheckedChange={toggleAllDocsHandler}
            disabled={isLoadingDocs || isAllDocIdsLoading}
          />
          <p>
            <strong>{selectedDocIds.size.toLocaleString()}</strong> Processed Docs selected
          </p>

          <Button size="sm" disabled={selectedDocIds.size === 0} onClick={deselectAllDocsFromDb}>
            Clear All ({selectedDocIds.size}) Selected Docs
          </Button>
        </div>

        <div className="flex items-center gap-5 pr-1">
          <Pagination
            canGetPrevPage={currentPage > 0}
            canGetNextPage={totalPages !== undefined ? currentPage < totalPages - 1 : (hasMore ?? false)}
            prevPage={prevPage}
            nextPage={nextPage}
            currentPage={currentPage}
            noOfPages={totalPages}
            goToPage={goToPage}
            onPageSizeChange={onPageSizeChange}
            pageSize={pageSize}
            totalCount={totalUnfilteredCount}
            filteredCount={totalCount}
            entityName="documents/folders"
          />

          <Tooltip>
            <TooltipTrigger asChild>
              <label
                className={cn(
                  'flex items-center gap-1.25 text-xs font-medium text-gray-500',
                  forcedFlatByFilters ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                )}
              >
                <Switch
                  checked={!isFlattened && !forcedFlatByFilters}
                  disabled={forcedFlatByFilters}
                  onCheckedChange={(checked) => setIsFlattened(!checked)}
                  className="scale-80"
                />
                Show folder structure
              </label>
            </TooltipTrigger>
            {forcedFlatByFilters && <TooltipContent>Disabled while filters are applied</TooltipContent>}
          </Tooltip>
        </div>
      </div>

      <DeleteDocumentsDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={() => {
          if (selectedDocIds.size > 0) {
            deleteDocs(Array.from(selectedDocIds));
          }
        }}
        isDeleting={isDeleting}
        selectedCount={selectedDocIds.size}
        hasInFlightDeletion={hasDeletionInFlight}
      />

      <SelectFromListDialog
        open={selectFromListOpen}
        onOpenChange={setSelectFromListOpen}
        matterId={matterId}
        source={selectFromListSource}
        onApplyMatchedDocIds={onAddSelectedDocIds}
        inputDescription="Upload a CSV file to add matching documents to your current selection."
      />

      {relativityWorkspaceId !== null && (
        <CreateRelativityListDialog
          open={createRelativityListOpen}
          onOpenChange={setCreateRelativityListOpen}
          workspaceId={relativityWorkspaceId}
          matterId={matterId}
          docIds={Array.from(selectedDocIds)}
        />
      )}
    </PageContainer>
  );
}
